import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ProductForm } from "@/components/admin/ProductForm";
import { api, euro, formatError } from "@/lib/api";

const Row = ({ p, onEdit, onRemove }) => (
  <div data-testid={`admin-product-row-${p.id}`} className={`flex items-center gap-4 p-4 ${p.active === false ? "opacity-60" : ""}`}>
    <img src={p.image} alt="" className="h-14 w-14 shrink-0 bg-[#0b120e] object-cover" />
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold">{p.name}</p>
      <p className="font-mono text-xs text-[#a3b1aa]">
        {p.category} · {p.is_digital ? "digitale" : `stock ${p.stock}`}
        {p.active === false && <span data-testid={`admin-product-hidden-${p.id}`} className="ml-2 text-[#f0a78f]">· nascosto</span>}
        {p.coming_soon && <span className="ml-2 text-[#e8d48b]">· in arrivo</span>}
      </p>
    </div>
    <span className="hidden font-mono text-sm text-[#c4dcca] sm:block">{euro(p.price)}</span>
    <button data-testid={`edit-product-${p.id}`} onClick={() => onEdit(p)} className="p-2 hover:text-[#c4dcca]" aria-label="Modifica"><Pencil size={16} /></button>
    <button data-testid={`delete-product-${p.id}`} onClick={() => onRemove(p)} className="p-2 hover:text-red-400" aria-label="Elimina"><Trash2 size={16} /></button>
  </div>
);

export const ProductsPanel = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, data);
      else await api.post("/admin/products", data);
      toast.success("Prodotto salvato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.name}"? Per toglierlo solo dal sito usa «Visibile sul sito».`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Prodotto eliminato");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const visible = products.filter((p) => p.active !== false);
  const hidden = products.filter((p) => p.active === false);

  return (
    <div data-testid="products-panel" className="space-y-8">
      <button data-testid="add-product-btn" onClick={() => { setEditing(null); setOpen(true); }}
        className="flex items-center gap-2 bg-[#98bfa2] px-5 py-3 font-display text-base font-800 uppercase text-[#050807] hover:bg-[#c4dcca]">
        <Plus size={16} /> Nuovo prodotto
      </button>
      {[["Sul sito", visible, "products-visible"], ["Nascosti", hidden, "products-hidden"]].map(([title, list, tid]) => list.length > 0 && (
        <section key={tid} data-testid={tid}>
          <p className="mb-3 font-mono text-xs uppercase tracking-widest text-[#98bfa2]">{title} · {list.length}</p>
          <div className="divide-y divide-white/5 border border-[rgba(152,191,162,0.15)]">
            {list.map((p) => <Row key={p.id} p={p} onEdit={(x) => { setEditing(x); setOpen(true); }} onRemove={remove} />)}
          </div>
        </section>
      ))}
      <ProductForm open={open} onOpenChange={setOpen} initial={editing} onSave={save} saving={saving} />
    </div>
  );
};
